import {
  LANG_COOKIE_NAME,
  LANG_STORAGE_KEY,
  LEGACY_LANG_STORAGE_KEY,
  normalizeLocale,
  type Locale,
} from '@/i18n/config'
import BrandLogoIcon from '@/assets/brand/logo.svg'
import { memo, useCallback, useMemo, useState } from 'react'

const maintenanceCopy: Record<
  Locale,
  {
    status: string
    title: string
    subtitle: string
    startLabel: string
    startValue: string
    restoreLabel: string
    restoreValue: string
    restoreHint: string
    impactTitle: string
    impactBody: string
    upgradeTitle: string
    upgradeBody: string
    refresh: string
    support: string
    note: string
    languageLabel: string
    nextLanguage: string
  }
> = {
  en: {
    status: 'Scheduled downtime',
    title: 'OpenCreator Website & Product Scheduled Downtime',
    subtitle:
      'To prepare for the launch of the all-new OpenCreator with Agent, the OpenCreator website and product services are temporarily unavailable.',
    startLabel: 'Downtime starts',
    startValue: 'June 7, 2026, 12:00 AM PT',
    restoreLabel: 'Expected restoration',
    restoreValue: 'June 7, 2026, 5:00 PM PT',
    restoreHint: 'Beijing Time: June 8, 2026, 8:00 AM',
    impactTitle: 'What is affected',
    impactBody: 'During the upgrade, the OpenCreator website and all product features are inaccessible.',
    upgradeTitle: 'What is coming',
    upgradeBody:
      'Once the upgrade is complete, you will be able to experience the brand-new OpenCreator with Agent. We cannot wait to share it with you.',
    refresh: 'Refresh page',
    support: 'Contact support',
    note: 'We sincerely apologize for the interruption and appreciate your patience and understanding.',
    languageLabel: 'Language',
    nextLanguage: '中文',
  },
  zh: {
    status: '正在更新中',
    title: 'OpenCreator 官网及产品停机升级通知',
    subtitle:
      '为了上线全新的 OpenCreator Agent 版本，我们正在对 OpenCreator 官网及产品服务进行全面停机升级。',
    startLabel: '升级开始时间',
    startValue: '北京时间 2026 年 6 月 7 日 15:00',
    restoreLabel: '预计恢复时间',
    restoreValue: '北京时间 2026 年 6 月 8 日 08:00',
    restoreHint: 'Pacific Time: June 7, 2026, 5:00 PM',
    impactTitle: '升级期间影响',
    impactBody: '升级期间，OpenCreator 官网及产品内的所有功能将暂时无法访问和使用。',
    upgradeTitle: '升级完成后',
    upgradeBody:
      '恢复后，大家将可以体验带有全新 Agent 的 OpenCreator。敬请期待！',
    refresh: '刷新页面',
    support: '联系支持',
    note: '对于由此带来的不便与等待，我们深感抱歉，也感谢大家的理解与耐心。',
    languageLabel: '语言',
    nextLanguage: 'English',
  },
}

const readInitialLocale = (): Locale => {
  try {
    const cookieLang = document.cookie.match(/(?:^|; )lang=(en|zh)(?:;|$)/)?.[1]
    const storedLang =
      window.localStorage.getItem(LANG_STORAGE_KEY) ??
      window.localStorage.getItem(LEGACY_LANG_STORAGE_KEY)

    return normalizeLocale(cookieLang ?? storedLang ?? navigator.language)
  } catch {
    return 'en'
  }
}

const persistLocale = (locale: Locale) => {
  document.documentElement.lang = locale
  window.localStorage.setItem(LANG_STORAGE_KEY, locale)
  window.localStorage.setItem(LEGACY_LANG_STORAGE_KEY, locale)
  document.cookie = `${LANG_COOKIE_NAME}=${locale}; path=/; max-age=${365 * 24 * 60 * 60}; SameSite=Lax`
}

export const MaintenancePage = memo(function MaintenancePage() {
  const [locale, setLocale] = useState<Locale>(readInitialLocale)
  const copy = maintenanceCopy[locale]

  const handleRefresh = useCallback(() => {
    window.location.reload()
  }, [])

  const toggleLocale = useCallback(() => {
    setLocale(current => {
      const nextLocale = current === 'zh' ? 'en' : 'zh'
      persistLocale(nextLocale)
      return nextLocale
    })
  }, [])

  const languageText = useMemo(
    () => `${copy.languageLabel} / ${copy.nextLanguage}`,
    [copy.languageLabel, copy.nextLanguage]
  )

  return (
    <main className="relative flex min-h-screen items-center justify-center overflow-hidden bg-ds-page-bg-2 px-5 py-10 text-ds-ink">
      <div className="pointer-events-none absolute -left-24 top-[-120px] h-72 w-72 rounded-full bg-ds-yellow-s/40 blur-3xl" />
      <div className="pointer-events-none absolute bottom-[-140px] right-[-100px] h-80 w-80 rounded-full bg-ds-blue-s blur-3xl" />
      <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-ds-gray-300 to-transparent" />

      <button
        type="button"
        onClick={toggleLocale}
        className="absolute right-5 top-5 z-10 inline-flex h-9 items-center justify-center rounded-full border border-ds-gray-200 bg-ds-surface/85 px-3 text-[12px] font-bold text-ds-gray-700 shadow-ds-1 backdrop-blur-sm transition hover:border-ds-gray-300 hover:bg-ds-surface hover:text-ds-ink focus:outline-none focus:ring-2 focus:ring-ds-gray-300 focus:ring-offset-2 sm:right-8 sm:top-8"
        aria-label={languageText}
      >
        {languageText}
      </button>

      <section className="relative w-full max-w-[760px] overflow-hidden rounded-[28px] border border-ds-gray-200 bg-ds-surface px-6 py-8 text-center shadow-ds-4 sm:px-10 sm:py-11">
        <div className="pointer-events-none absolute inset-x-8 top-0 h-px bg-gradient-to-r from-transparent via-ds-gray-200 to-transparent" />
        <div className="pointer-events-none absolute left-1/2 top-0 h-40 w-40 -translate-x-1/2 -translate-y-1/2 rounded-full bg-ds-ink/[0.04] blur-2xl" />

        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-[20px] bg-ds-ink text-ds-surface shadow-ds-2">
          <BrandLogoIcon className="h-9 w-9" aria-hidden="true" />
        </div>

        <div className="mx-auto mt-7 inline-flex items-center gap-2 rounded-full border border-ds-gray-200 bg-ds-surface-soft px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.18em] text-ds-gray-600">
          <span className="h-1.5 w-1.5 rounded-full bg-ds-yellow" />
          {copy.status}
        </div>

        <h1 className="mx-auto mt-5 max-w-[620px] text-[28px] font-black leading-tight tracking-[-0.04em] text-ds-ink sm:text-[36px]">
          {copy.title}
        </h1>

        <p className="mx-auto mt-4 max-w-[590px] text-[14px] font-medium leading-7 text-ds-gray-600 sm:text-[15px]">
          {copy.subtitle}
        </p>

        <div className="mx-auto mt-7 grid max-w-[620px] gap-3 text-left sm:grid-cols-2">
          <div className="rounded-[18px] border border-ds-gray-200 bg-ds-surface-soft p-4">
            <div className="text-[12px] font-bold text-ds-gray-500">{copy.startLabel}</div>
            <div className="mt-2 text-[16px] font-black leading-snug text-ds-ink">
              {copy.startValue}
            </div>
          </div>

          <div className="rounded-[18px] border border-ds-ink bg-ds-ink p-4 text-ds-surface shadow-ds-2">
            <div className="text-[12px] font-bold text-ds-surface/65">{copy.restoreLabel}</div>
            <div className="mt-2 text-[17px] font-black leading-snug">{copy.restoreValue}</div>
            <div className="mt-2 text-[12px] font-semibold text-ds-surface/60">
              {copy.restoreHint}
            </div>
          </div>
        </div>

        <div className="mx-auto mt-5 grid max-w-[620px] gap-3 text-left sm:grid-cols-2">
          <div className="rounded-[18px] border border-ds-gray-200 bg-ds-surface p-4">
            <div className="text-[13px] font-black text-ds-ink">{copy.impactTitle}</div>
            <p className="mt-2 text-[13px] font-medium leading-6 text-ds-gray-600">
              {copy.impactBody}
            </p>
          </div>

          <div className="rounded-[18px] border border-ds-gray-200 bg-ds-surface p-4">
            <div className="text-[13px] font-black text-ds-ink">{copy.upgradeTitle}</div>
            <p className="mt-2 text-[13px] font-medium leading-6 text-ds-gray-600">
              {copy.upgradeBody}
            </p>
          </div>
        </div>

        <div className="mx-auto mt-7 flex max-w-[360px] flex-col gap-3 sm:flex-row sm:justify-center">
          <button
            type="button"
            onClick={handleRefresh}
            className="inline-flex h-11 items-center justify-center rounded-[14px] bg-ds-ink px-5 text-[13px] font-bold text-ds-surface transition hover:bg-ds-ink-hover focus:outline-none focus:ring-2 focus:ring-ds-ink focus:ring-offset-2"
          >
            {copy.refresh}
          </button>
          <a
            href="mailto:support@opencreator.io"
            className="inline-flex h-11 items-center justify-center rounded-[14px] border border-ds-gray-200 bg-ds-surface px-5 text-[13px] font-bold text-ds-gray-700 transition hover:border-ds-gray-300 hover:bg-ds-surface-soft hover:text-ds-ink focus:outline-none focus:ring-2 focus:ring-ds-gray-300 focus:ring-offset-2"
          >
            {copy.support}
          </a>
        </div>

        <p className="mt-6 text-[12px] font-semibold text-ds-gray-400">{copy.note}</p>
      </section>
    </main>
  )
})
